import TextCell from '@components/ListV2/components/table/cells/TextCell';
import StatusChip from '@components/v2/ui/StatusChip/StatusChip';
import Typography from '@components/v2/ui/Typography/Typography';
import { OrderBy } from '../../../components/Hooks/useList.types';
import { Column } from '../../../components/ListV2/components/table/VirtualizedTable.types';
import AppEnvironment from '../../../components/enums/appEnvironment.enum';
import LangEnum from '../../../lang/LangEnum';
import PATHS from '../../../routes/routes.const';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import { truncateTwoLinesStyles } from '../../../useTeceoStyles';
import { formatDate } from '../../../utils/dates';
import { formatCNPJ, formatCPF } from '../../../utils/formatters';
import { loggedAsStore } from '../../context/contextUtils';
import { CUSTOMER_STATUS_CHIP_CONFIG } from '../../customer/customerStatus/customerStatusChip.constants';
import { AvailabilityEnum } from '../../products/ProductTypes';
import { Ability } from '../../users/ability.types';
import { hasSplit } from '../conditions/orderCondition.utils';
import { OrderListWithBaseItem } from '../order.types';
import { getOrderView } from '../ordersView.utils';
import OrderTags from './OrderTags';
import CustomerLinkCell from './components/CustomerLinkCell';
import { ORDER_INTEGRATION_STATUS_CHIP_CONFIG } from './components/orderIntegrationStatusChip.constants';
import { ORDER_LIST_STATUS_CHIP_CONFIG } from './components/orderStatusChip.constants';
import { GetColumnParams, GetRef, RenderStatusChipCellParams } from './order-list.types';
import { orderCodeColumnName, orderStatusColumnName } from './orderList.consts';
import { OrderListTabName, getOrderStatusByTab } from './orderListTabs';
import { combineTotals } from './utils/orderCombineTotals.utils';

export const ORDER_DEFAULT_ORDER: OrderBy = { createdAt: 'DESC' };

type AbilityGetHrefType = {
  disabled: boolean;
} & GetRef;

const getHref = ({
  orderId,
  orderStatus,
  role,
  team,
  orderIntegrationStatus,
  disabled,
}: AbilityGetHrefType) => {
  const appEnvironment = useAppEnvironmentStore.getState().appEnvironment;

  return disabled
    ? undefined
    : `${PATHS.main.orders.path}/${orderId}/${getOrderView({
        role,
        orderStatus,
        appEnvironment,
        orderIntegrationStatus,
        integrations: team?.brand?.integrations,
      })}`;
};

const renderStatusChipCell = ({ config, status, href }: RenderStatusChipCellParams) => {
  const statusConfig = status ? config[status] : undefined;

  return {
    node: statusConfig ? <StatusChip {...statusConfig} size="small" variant="plain" /> : '-',
    href,
  };
};

const getOrderHref = (item: OrderListWithBaseItem, params: GetColumnParams, ability: Ability) =>
  getHref({
    disabled: ability.cannot('read', 'order-details'),
    orderId: item.orderId,
    orderIntegrationStatus: item.orderSynchronized,
    orderStatus: item.orderStatus,
    role: params.role,
    team: params.team,
  });

const getOrderListColumns = (params: GetColumnParams): Column<OrderListWithBaseItem>[] => {
  const customerNameColumnName = 'customerCommercialName';
  const repColumnName = 'orderSalesRepresentativeName';
  const { ability } = params;
  const appEnvironment = useAppEnvironmentStore.getState().appEnvironment;

  const summarizeTotal = () => {
    const orderStatus = getOrderStatusByTab(params.selectedTab);
    const allTotals = combineTotals(params.totals, params.offlineTotals);

    return allTotals.reduce(
      (acc, total) => {
        if (
          params.selectedTab === OrderListTabName.ALL ||
          orderStatus?.includes(total.orderStatus)
        ) {
          acc.totalDiscountValue += total.totalDiscountValue;
          acc.totalGrossValue += total.totalGrossValue;
          acc.totalNetValue += total.totalNetValue;
          acc.totalPieces += total.totalPieces;
          acc.totalShippingCost += total.totalShippingCostValue;
          acc.totalCharges += total.totalChargesValue;
          acc.totalItemsGrossValue += total.totalItemsGrossValue;
          acc.totalItemsDiscountValue += total.totalItemsDiscountValue;
        }
        return acc;
      },
      {
        totalDiscountValue: 0,
        totalGrossValue: 0,
        totalNetValue: 0,
        totalPieces: 0,
        totalCharges: 0,
        totalShippingCost: 0,
        totalItemsGrossValue: 0,
        totalItemsDiscountValue: 0,
      },
    );
  };

  const summarizedTotal = summarizeTotal();

  const orderCode: Column<OrderListWithBaseItem> = {
    name: orderCodeColumnName,
    title: LangEnum.GLOBAL_CODE,
    sortable: true,
    width: 128,
    canHide: false,
    render: (item) => ({
      node: (
        <Typography noWrap title={item.orderCode || '-'}>
          {item.orderCode || '-'}
        </Typography>
      ),
      href: getOrderHref(item, params, ability),
    }),
  };

  const orderErpCode: Column<OrderListWithBaseItem> = {
    name: 'orderExternalCode',
    title: LangEnum.GLOBAL_ERP_CODE,
    sortable: true,
    width: 136,
    showByDefault: false,
    hide: !params.hasIntegrations,
    render: (item) => ({
      node: (
        <Typography noWrap title={item.orderExternalCode || '-'}>
          {item.orderExternalCode || '-'}
        </Typography>
      ),
      href: getOrderHref(item, params, ability),
    }),
  };

  const orderBrand: Column<OrderListWithBaseItem> = {
    name: 'orderBrand',
    title: LangEnum.GLOBAL_BRAND,
    width: 150,
    canHide: false,
    hide: appEnvironment !== AppEnvironment.STORE,
    render: (item) => ({
      node: (
        <Typography noWrap title={item.brandCommercialName}>
          {item.brandCommercialName}
        </Typography>
      ),
      href: getOrderHref(item, params, ability),
    }),
  };

  const customerName: Column<OrderListWithBaseItem> = {
    name: customerNameColumnName,
    title: LangEnum.GLOBAL_CUSTOMER,
    width: 256,
    xs: {
      fullWidth: true,
    },
    render: (item) => ({
      node: (
        <CustomerLinkCell
          customerCommercialName={item.customerCommercialName}
          customerCompanyName={item.customerCompanyName}
          customerId={item.customerId}
          disableLink={params.isOfflineMode || ability.cannot('menu', 'customers')}
        />
      ),
      href: getOrderHref(item, params, ability),
    }),
  };

  const salesRep: Column<OrderListWithBaseItem> = {
    name: repColumnName,
    title: LangEnum.GLOBAL_SALES_REPRESENTATIVE,
    width: 240,
    sortable: true,
    render: (item) => ({
      node: (
        <Typography noWrap title={item.orderSalesRepresentativeName || '-'}>
          {item.orderSalesRepresentativeName || '-'}
        </Typography>
      ),
      href: getOrderHref(item, params, ability),
    }),
  };

  const deliveryWindow: Column<OrderListWithBaseItem> = {
    name: 'deliveryWindow',
    title: LangEnum.GLOBAL_TYPE,
    width: 180,
    render: (item) => ({
      node: hasSplit(item.orderSplitSettings ?? []) ? (
        <OrderTags
          deliveryWindow={item.deliveryWindow}
          availabilities={(item?.availabilities as AvailabilityEnum[]) ?? []}
          classification={item?.classification}
        />
      ) : null,
      href: getOrderHref(item, params, ability),
    }),
  };

  const subTotal: Column<OrderListWithBaseItem> = {
    name: 'subTotal',
    title: LangEnum.GLOBAL_GROSS_ORDER_VALUE,
    width: 210,
    sortable: true,
    render: (item) => ({
      node: params.formatMoney(item.orderSubTotal) ?? '-',
      href: getOrderHref(item, params, ability),
    }),
    renderTopColumn: () => params.formatMoney(summarizedTotal?.totalGrossValue ?? 0),
  };

  const createdAt: Column<OrderListWithBaseItem> = {
    name: 'createdAt',
    title: LangEnum.GLOBAL_CREATED_AT,
    width: 112,
    sortable: true,
    defaultSort: 'DESC',
    render: (item) => ({
      node: formatDate(item.orderCreatedAt, 'dd/MM/yy'),
      href: getOrderHref(item, params, ability),
    }),
    renderTopColumn: () => <></>,
  };

  const updatedAt: Column<OrderListWithBaseItem> = {
    name: 'updatedAt',
    title: LangEnum.GLOBAL_UPDATED_AT,
    width: 136,
    sortable: true,
    renderTopColumn: () => <></>,
    render: (item) => ({
      node: formatDate(item.orderUpdatedAt, 'dd/MM/yy HH:mm'),
      href: getOrderHref(item, params, ability),
    }),
  };

  const orderStatus: Column<OrderListWithBaseItem> = {
    name: orderStatusColumnName,
    title: LangEnum.ORDER_STATUS_LABEL,
    width: 136,
    render: (item) =>
      renderStatusChipCell({
        config: ORDER_LIST_STATUS_CHIP_CONFIG,
        status: item.orderVirtualStatus ?? item.orderStatus,
        href: getOrderHref(item, params, ability),
      }),
    renderTopColumn: () => <></>,
  };

  if (params.isOfflineMode) {
    const offlineColumns = [
      orderCode,
      customerName,
      salesRep,
      deliveryWindow,
      subTotal,
      createdAt,
      updatedAt,
      orderStatus,
    ];

    return offlineColumns.map((column) => {
      return {
        ...column,
        sortable: false,
        canHide: false,
      };
    });
  }

  return [
    orderCode,
    orderErpCode,
    orderBrand,
    customerName,
    {
      name: 'customerCode',
      title: LangEnum.REPORT_HEADER_CUSTOMER_CODE,
      width: 120,
      showByDefault: false,
      render: (item) => ({
        node: item.customerCode || '-',
        href: getOrderHref(item, params, ability),
      }),
    },
    {
      name: 'customerCpfCnpj',
      title: LangEnum.ORDER_LIST_HEADER_CUSTOMER_CPF_CNPJ,
      width: 168,
      showByDefault: false,
      render: (item) => ({
        node: item.customerCpfCnpj
          ? item.customerIsPj
            ? formatCNPJ(item.customerCpfCnpj)
            : formatCPF(item.customerCpfCnpj)
          : '-',
        href: getOrderHref(item, params, ability),
      }),
    },
    {
      name: 'customerSuframaCode',
      title: LangEnum.GLOBAL_SUFRAMA_CODE,
      width: 120,
      showByDefault: false,
      render: (item) => ({
        node: (
          <Typography title={item.customerSuframaCode || '-'} noWrap>
            {item.customerSuframaCode || '-'}
          </Typography>
        ),
        href: getOrderHref(item, params, ability),
      }),
    },
    {
      name: 'customerStatus',
      title: LangEnum.ORDER_FILTER_CUSTOMER_STATUS_LABEL,
      width: 144,
      showByDefault: false,
      render: (item) =>
        renderStatusChipCell({
          config: CUSTOMER_STATUS_CHIP_CONFIG,
          status: item.customerStatus,
          href: getOrderHref(item, params, ability),
        }),
    },
    salesRep,
    deliveryWindow,
    {
      name: 'sellerName',
      title: LangEnum.GLOBAL_SELLER,
      width: 144,
      showByDefault: false,
      render: (item) => {
        const label = item.sellerName || item.sellerEmail || '-';
        return {
          node: (
            <Typography noWrap title={label}>
              {label}
            </Typography>
          ),
          href: getOrderHref(item, params, ability),
        };
      },
    },
    {
      name: 'totalPieces',
      title: LangEnum.REPORT_TOTALS_SUM_PIECES,
      width: 128,
      sortable: true,
      showByDefault: false,
      render: (item) => ({
        node: item.totalPieces?.toLocaleString(params.locale) ?? '-',
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => (summarizedTotal?.totalPieces ?? 0).toLocaleString(params.locale),
    },
    {
      name: 'orderItemsGrossTotalValue',
      title: LangEnum.GLOBAL_ITEM_GROSS_VALUE,
      width: 170,
      showByDefault: false,
      sortable: true,
      render: (item) => ({
        node: params.formatMoney(item.orderItemsGrossTotalValue ?? 0),
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalItemsGrossValue ?? 0),
    },
    {
      name: 'orderItemsTotalDiscountValue',
      title: LangEnum.ORDER_LIST_ITEMS_TOTAL_DISCOUNT_VALUE,
      width: 178,
      showByDefault: false,
      sortable: true,
      render: (item) => ({
        node: params.formatMoney(item.orderItemsTotalDiscountValue ?? 0),
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalItemsDiscountValue ?? 0),
    },
    subTotal,
    {
      name: 'orderDiscountValue',
      title: LangEnum.PAYMENT_METHOD_ORDER_DISCOUNT,
      width: 150,
      sortable: true,
      showByDefault: false,
      render: (item) => ({
        node: params.formatMoney(item.orderDiscountValue) ?? '-',
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalDiscountValue ?? 0),
    },
    {
      name: 'orderTotalCharges',
      title: LangEnum.GLOBAL_CHARGES,
      width: 150,
      sortable: true,
      showByDefault: false,
      render: (item) => ({
        node: params.formatMoney(item.orderTotalCharges ?? 0),
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalCharges ?? 0),
    },
    {
      name: 'orderTotalShippingCost',
      title: LangEnum.GLOBAL_SHIPPING_COST,
      width: 150,
      sortable: true,
      showByDefault: false,
      render: (item) => ({
        node: params.formatMoney(item.orderTotalShippingCost ?? 0),
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalShippingCost ?? 0),
    },
    {
      name: 'totalValue',
      title: LangEnum.REPORT_HEADER_ORDER_TOTAL,
      width: 150,
      showByDefault: false,
      render: (item) => ({
        node: params.formatMoney(item.orderTotalValue) ?? '-',
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => params.formatMoney(summarizedTotal?.totalNetValue ?? 0),
    },
    {
      name: 'orderCollections',
      title: LangEnum.COLLECTION_FORM_NAME_LABEL,
      width: 152,
      showByDefault: false,
      render: (item) => {
        const label = item.orderCollections?.join(', ') || '-';
        return {
          node: <TextCell firstField={label} />,
          href: getOrderHref(item, params, ability),
        };
      },
    },
    {
      name: 'orderCollectionsCodes',
      title: LangEnum.COLLECTION_FORM_CODE_LABEL,
      width: 136,
      showByDefault: false,
      render: (item) => {
        const label = item.orderCollectionsCodes?.join(', ') || '-';
        return {
          node: <TextCell firstField={label} />,
          href: getOrderHref(item, params, ability),
        };
      },
    },
    createdAt,
    updatedAt,
    orderStatus,
    {
      name: 'orderLastReason',
      title: LangEnum.GLOBAL_REASON,
      width: 190,
      showByDefault: false,
      hide: loggedAsStore(params.role),
      renderTopColumn: () => <></>,
      render: (item) => ({
        node: (
          <>
            {!item.orderLastReason ? (
              '-'
            ) : (
              <Typography
                variant="body2"
                title={item.orderLastReason}
                sx={{ ...truncateTwoLinesStyles, whiteSpace: 'normal' }}
              >
                {item.orderLastReason}
              </Typography>
            )}
          </>
        ),
        href: getOrderHref(item, params, ability),
      }),
    },
    {
      name: 'orderSynchronized',
      title: LangEnum.LIST_HEADER_INTEGRATION_STATUS,
      hide: loggedAsStore(params.role) || !params.hasIntegrations,
      width: 148,
      render: (item) =>
        renderStatusChipCell({
          config: ORDER_INTEGRATION_STATUS_CHIP_CONFIG,
          status: item.orderSynchronized,
          href: getOrderHref(item, params, ability),
        }),
      renderTopColumn: () => <></>,
    },
    {
      name: 'orderIntegratedAt',
      title: LangEnum.LIST_HEADER_INTEGRATION_DATE,
      hide: loggedAsStore(params.role) || !params.hasIntegrations,
      width: 144,
      sortable: true,
      render: (item) => ({
        node: formatDate(item.orderIntegratedAt, 'dd/MM/yy HH:mm') || '-',
        href: getOrderHref(item, params, ability),
      }),
      renderTopColumn: () => <></>,
    },
  ];
};

export default getOrderListColumns;
